import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import { InputNumber } from "primereact/inputnumber";
import { Slider } from "primereact/slider";
import Web3 from "web3";
import BigNumber from "bignumber.js";
import routerAbi from "../../contracts/routerAbi";
import erc20Abi from "../../contracts/ierc20Abi";
import { toast } from "react-toastify";
import { formatCurrencyExtended, formatDecimals } from "../../utils";

/* ─────────────────────────────────────────────────────────── */

BigNumber.config({ DECIMAL_PLACES: 40, EXPONENTIAL_AT: 1e9 });

const web3 = new Web3("https://binance.llamarpc.com");
const WBNB = "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c";
const ROUTER = "0x10ED43C718714eb63d5aA57B78B54704E256024E";
const DEADLINE_SECS = 60 * 15; // 15-min
const GAS_BUFFER_BNB = new BigNumber(0.0025);
const DEFAULT_SLIPPAGE = 5;
const PERCENTS = [25, 50, 75, 100];

/* dummy – extend at will */
const getTokenFee = (addr) => ({ buyFee: 200, sellFee: 200 });

export default function QuickSwap({
  token, // { address, symbol, decimals, price, balanceOfHolder }
  bnbBalance, // wei string | number
  bnbPrice, // USD price of BNB  ← passed from Wallet
  walletAddress,
  getLiquidityPairs,
  getTokenLogoUrl, // helper from Wallet  ← passed from Wallet
}) {
  const [visible, setVisible] = useState(false);
  const [sideBuy, setSideBuy] = useState(true); // BUY ⇄ SELL
  const [amountStr, setAmountStr] = useState(""); // user input
  const [slippage, setSlippage] = useState(DEFAULT_SLIPPAGE);
  const [processing, setProcessing] = useState(false);

  const [bestPath, setBestPath] = useState([]);
  const [estOutWei, setEstOutWei] = useState("0");
  const [loadingPath, setLoadingPath] = useState(false);

  const router = useMemo(() => new web3.eth.Contract(routerAbi, ROUTER), []);

  const tokenDecimals = token?.decimals ?? 18;
  const bnInput = useMemo(() => new BigNumber(amountStr || 0), [amountStr]);
  const amountInWei = useMemo(() => {
    if (bnInput.isZero()) return "0";
    return sideBuy
      ? web3.utils.toWei(bnInput.toFixed(18), "ether") // BNB
      : bnInput.times(new BigNumber(10).pow(tokenDecimals)).toFixed(0); // token
  }, [bnInput, sideBuy, tokenDecimals]);

  const bnbBalBN = useMemo(() => new BigNumber(bnbBalance || 0), [bnbBalance]);
  const tokenBalBN = useMemo(
    () => new BigNumber(token.balanceOfHolder || 0),
    [token]
  );
  const bnbBalHuman = formatDecimals(bnbBalBN, 18);
  const tokenBalHuman = formatDecimals(tokenBalBN, tokenDecimals);
  const bnbBalUSD = bnbBalBN.div(1e18).times(bnbPrice || 0);
  const tokenBalUSD = tokenBalBN
    .div(new BigNumber(10).pow(tokenDecimals))
    .times(token.price || 0);

  // Helper for % buttons
  const handlePercent = (percent) => {
    if (sideBuy) {
      const spendable = bnbBalBN.minus(GAS_BUFFER_BNB.times(1e18));
      const amt = spendable.isGreaterThan(0)
        ? spendable.div(1e18).times(percent / 100)
        : new BigNumber(0);
      setAmountStr(
        amt.isGreaterThan(0)
          ? amt.decimalPlaces(6, BigNumber.ROUND_FLOOR).toString()
          : ""
      );
    } else {
      const amt = tokenBalBN
        .div(new BigNumber(10).pow(tokenDecimals))
        .times(percent / 100);
      setAmountStr(
        amt.isGreaterThan(0)
          ? amt
              .decimalPlaces(
                tokenDecimals > 6 ? 6 : tokenDecimals,
                BigNumber.ROUND_FLOOR
              )
              .toString()
          : ""
      );
    }
  };

  /* --------------- path discovery with loading spinner -------- */
  const fetchBestPath = useCallback(
    async (ctrl) => {
      setLoadingPath(true);
      try {
        if (bnInput.isZero() || !walletAddress) {
          setBestPath([]);
          setEstOutWei("0");
          setLoadingPath(false);
          return;
        }
        const pairs = await getLiquidityPairs(token);
        if (ctrl.signal.aborted) {
          setLoadingPath(false);
          return;
        }

        const addrMeta = {};
        pairs.forEach((p) => {
          addrMeta[p.token.toLowerCase()] = {
            symbol: p.tokenSymbol,
            logo: getTokenLogoUrl({ address: p.token, symbol: p.tokenSymbol }),
          };
          addrMeta[p.backingToken.toLowerCase()] = {
            symbol: p.backingTokenSymbol,
            logo: getTokenLogoUrl({
              address: p.backingToken,
              symbol: p.backingTokenSymbol,
            }),
          };
        });
        addrMeta[token.address.toLowerCase()] = {
          symbol: token.symbol,
          logo: getTokenLogoUrl({
            address: token.address,
            symbol: token.symbol,
          }),
        };
        addrMeta[WBNB] = {
          symbol: "BNB",
          logo: getTokenLogoUrl({ address: WBNB, symbol: "BNB" }),
        };

        const candidates = [
          sideBuy ? [WBNB, token.address] : [token.address, WBNB],
        ];
        pairs.forEach((p) => {
          const x = p.backingToken;
          if (!x || x.toLowerCase() === WBNB) return;
          candidates.push(
            sideBuy ? [WBNB, x, token.address] : [token.address, x, WBNB]
          );
        });

        let best = { out: new BigNumber(0), path: [] };

        for (const path of candidates) {
          try {
            const amounts = await router.methods
              .getAmountsOut(amountInWei, path)
              .call();
            if (ctrl.signal.aborted) {
              setLoadingPath(false);
              return;
            }
            let out = new BigNumber(amounts[amounts.length - 1]);
            if (path.length === 3) {
              const inter = getTokenFee(path[1]);
              const finl = getTokenFee(sideBuy ? token.address : path[1]);
              out = out
                .times(10000 - inter.sellFee)
                .times(10000 - finl.buyFee)
                .div(1e8);
            } else {
              const f = getTokenFee(token.address);
              out = out
                .times(10000 - (sideBuy ? f.buyFee : f.sellFee))
                .div(10000);
            }
            if (out.gt(best.out)) best = { out, path };
          } catch (_) {
            /* ignore */
          }
        }
        const pathWithMeta = best.path.map((a) => {
          const m = addrMeta[a.toLowerCase()] || {
            symbol: a.slice(0, 6) + "…",
            logo: "",
          };
          return { ...m, address: a };
        });
        setBestPath(pathWithMeta);
        setEstOutWei(best.out.toFixed(0));
      } catch (err) {
        console.error(err);
        setBestPath([]);
        setEstOutWei("0");
      }
      setLoadingPath(false);
    },
    [
      bnInput,
      amountInWei,
      sideBuy,
      token,
      walletAddress,
      getLiquidityPairs,
      router,
      getTokenLogoUrl,
    ]
  );

  useEffect(() => {
    const c = new AbortController();
    fetchBestPath(c);
    return () => c.abort();
  }, [fetchBestPath]);

  /* ------------------------------------------------ execute swap ----- */
  const doSwap = async () => {
    try {
      if (processing) return;
      if (!walletAddress) throw new Error("Connect wallet first");
      if (bnInput.isZero()) throw new Error("Enter amount");
      if (!bestPath.length) throw new Error("No route");

      setProcessing(true);
      const minOutWei = new BigNumber(estOutWei)
        .times(100 - slippage)
        .div(100)
        .integerValue(BigNumber.ROUND_FLOOR)
        .toFixed(0);

      if (!sideBuy) {
        const erc20 = new web3.eth.Contract(erc20Abi, token.address);
        const allowance = new BigNumber(
          await erc20.methods.allowance(walletAddress, ROUTER).call()
        );
        if (allowance.lt(amountInWei)) {
          toast.info("Approving token…");
          await erc20.methods
            .approve(ROUTER, web3.utils.toTwosComplement(-1))
            .send({ from: walletAddress });
        }
      }

      const deadline = Math.floor(Date.now() / 1000) + DEADLINE_SECS;
      toast.info("Submitting swap…");

      if (sideBuy) {
        await router.methods
          .swapExactETHForTokensSupportingFeeOnTransferTokens(
            minOutWei,
            bestPath.map((p) => p.address),
            walletAddress,
            deadline
          )
          .send({ from: walletAddress, value: amountInWei });
      } else {
        await router.methods
          .swapExactTokensForETHSupportingFeeOnTransferTokens(
            amountInWei,
            minOutWei,
            bestPath.map((p) => p.address),
            walletAddress,
            deadline
          )
          .send({ from: walletAddress });
      }
      toast.success("Swap confirmed 🎉");
      setAmountStr("");
      setVisible(false);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Swap failed");
    } finally {
      setProcessing(false);
    }
  };

  const bnbReadable = new BigNumber(web3.utils.fromWei(bnbBalance || "0"));
  const buyDisabled = sideBuy && bnInput.plus(GAS_BUFFER_BNB).gt(bnbReadable);

  const estDisplay = useMemo(() => {
    if (estOutWei === "0") return "—";
    return sideBuy
      ? new BigNumber(estOutWei)
          .div(new BigNumber(10).pow(tokenDecimals))
          .toFixed(4)
      : web3.utils.fromWei(estOutWei);
  }, [estOutWei, sideBuy, tokenDecimals]);

  /* ------------ helpers for “estimate” panel ------------ */

    /* pretty-print helpers ---------------------------------------------- */
    const fmt = (bn, dp = 4) =>                // 1 234 567.8910
    bn.isFinite() ? bn.toFormat(dp) : bn.toFixed(dp);

    const usd = (bn) => `(${formatCurrencyExtended(bn)})`;   // ($123.45)


  const tokenLogo = getTokenLogoUrl({
    address: token.address,
    symbol: token.symbol,
  });
  const bnbLogo = getTokenLogoUrl({ address: WBNB, symbol: "BNB" });

  const estOutHumanBN = useMemo(() => {
    if (estOutWei === "0") return new BigNumber(0);
    return sideBuy
      ? new BigNumber(estOutWei).div(new BigNumber(10).pow(tokenDecimals)) // token
      : new BigNumber(estOutWei).div(1e18); // BNB
  }, [estOutWei, sideBuy, tokenDecimals]);

  const receiveLogo = sideBuy ? tokenLogo : bnbLogo;
  const receiveSymbol = sideBuy ? token.symbol : "BNB";
  const receiveBalBN = sideBuy
    ? tokenBalBN.div(new BigNumber(10).pow(tokenDecimals))
    : bnbBalBN.div(1e18);

  const newBalBN = receiveBalBN.plus(estOutHumanBN);
  const receiveBalStr = formatDecimals(receiveBalBN,0); // current wallet bal
  const newBalStr     = formatDecimals(newBalBN, 0); // after-swap bal
  const estOutStr     = formatDecimals(estOutHumanBN, 0); // already rounded for display

  /* USD helpers --------------------------------------------------------- */
  const receivePrice = sideBuy ? token.price || 0 : bnbPrice || 0; // $/unit
  const estOutUSDStr = formatCurrencyExtended(
    estOutHumanBN.times(receivePrice)
  );
  const receiveBalUSDStr = formatCurrencyExtended(
    receiveBalBN.times(receivePrice)
  );
  const newBalUSDStr = formatCurrencyExtended(newBalBN.times(receivePrice));

  /* which symbol/amount for pay header and % buttons */
  const payLogo = sideBuy ? bnbLogo : tokenLogo;
  const paySymbol = sideBuy ? "BNB" : token.symbol;
  const payBal = sideBuy ? bnbBalHuman : tokenBalHuman;
  const payBalUSD = sideBuy ? bnbBalUSD : tokenBalUSD;

  /* ---- live $ value for what the user typed -------------------------------- */
    const payUnitPrice = sideBuy ? (bnbPrice || 0) : (token.price || 0);   // $/BNB | $/token
    const payUsdStr    = useMemo(() =>
    bnInput.isZero()
        ? ""                               // nothing while empty / zero
        : formatCurrencyExtended(bnInput.times(payUnitPrice))
    , [bnInput, payUnitPrice]);


  return (
    <>
      <Button
        label="Quick Swap" //className="p-button-primary p-button-sm"
        onClick={() => setVisible(true)}
        style={{ width: "100%", marginTop: "4px" }}
      />

      <Dialog
        visible={visible}
        onHide={() => !processing && setVisible(false)}
        draggable={false}
        closable={!processing}
        style={{ width: 420 }}
        header={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <img src={sideBuy ? bnbLogo : tokenLogo} alt="" width={22} />
              <span style={{ fontSize: "22px", color: "#999" }}>&#8594;</span>
              <img src={sideBuy ? tokenLogo : bnbLogo} alt="" width={22} />
              <h4 style={{ margin: "0 0 0 12px", fontWeight: 600 }}>
                {sideBuy ? `Buy ${token.symbol}` : `Sell ${token.symbol}`}
              </h4>
            </div>
            <InputSwitch
              checked={sideBuy}
              onChange={(e) => {
                setSideBuy(e.value);
                setAmountStr("");
              }}
              tooltip="BUY / SELL"
            />
          </div>
        }
        footer={
          <div style={{ textAlign: "right" }}>
            <Button
              label={processing ? "Processing…" : sideBuy ? "Buy" : "Sell"}
              icon={processing ? "pi pi-spin pi-spinner" : undefined}
              className="p-button-success"
              disabled={
                processing ||
                bnInput.isZero() ||
                !bestPath.length ||
                buyDisabled
              }
              onClick={doSwap}
            />
          </div>
        }
      >
        {/* overlay loader */}
        {/* {loadingPath && (
          <div style={{
            position:"absolute", left:0, top:0, right:0, bottom:0,
            background:"rgba(255,255,255,0.75)",
            zIndex:10, display:"flex", alignItems:"center", justifyContent:"center"
          }}>
            <i className="pi pi-spin pi-spinner" style={{fontSize:"2rem",color:"#666"}}/>
            <span style={{marginLeft:12}}>Routing…</span>
          </div>
        )} */}

        {/* amount input + pay balances + % buttons */}
        <div className="p-field" style={{ marginBottom: 12 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 2,
            }}
          >
            <label
              style={{
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {/* Pay  */}
              <img src={payLogo} alt="" width={16} />
              {paySymbol}
              {/* <span style={{color:"#888",fontWeight:400,marginLeft:8}}>
                {payBal}&nbsp;•&nbsp;{formatCurrencyExtended(payBalUSD)}
              </span> */}
            </label>

            <span style={{ color: "#888", fontWeight: 400, marginLeft: 8 }}>
              {payBal}&nbsp;•&nbsp;{formatCurrencyExtended(payBalUSD)}
            </span>
          </div>
         <InputNumber
                value={amountStr}
                mode="decimal"
                onChange={(e)=>setAmountStr(e.value)}
                minFractionDigits={0}
                maxFractionDigits={tokenDecimals}
                placeholder="0.0"
                disabled={processing}
                style={{width:"100%"}}
                />

                {/* ≈ USD helper */}
                {!!payUsdStr && (
                <div style={{textAlign:"right",
                            fontSize:".8rem",
                            color:"#9fa6b2",
                            marginTop:4}}>
                    ≈ {payUsdStr}
                </div>
                )}
          <div
            style={{
              display: "flex",
              gap: 4,
              alignSelf: "flex-end",
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 4,
              marginBottom: 8,
            }}
          >
            {PERCENTS.map((pct) => (
              <Button
                key={pct}
                type="button"
                label={pct === 100 ? "MAX" : `${pct}%`}
                className="p-button-text p-button-sm"
                style={{ fontSize: ".8rem", padding: "2px 6px", borderRadius: 10, border: "1px solid #eee" }}
                onClick={() => handlePercent(pct)}
                disabled={processing}
              />
            ))}
          </div>
        </div>

        {/* balances (again, for quick reference) */}
        {/* <div style={{fontSize:".8rem",marginBottom:"10px",lineHeight:"1.3"}}>
          <div>
            <img src={bnbLogo} alt="" width={14}/> 
            {bnbBalHuman} BNB&nbsp;•&nbsp;{formatCurrencyExtended(bnbBalUSD)}
          </div>
          <div>
            <img src={tokenLogo} alt="" width={14}/> 
            {tokenBalHuman} {token.symbol}&nbsp;•&nbsp;{formatCurrencyExtended(tokenBalUSD)}
          </div>
        </div> */}

        {/* slippage */}
        {/* <div style={{ marginTop: 8, marginBottom: 25 }}>
          <label>Slippage {slippage}%</label>
          <Slider
            value={slippage}
            onChange={(e) => setSlippage(e.value)}
            min={1}
            max={15}
            step={0.5}
          />
        </div> */}

        {/* estimate ---------------------------------------------------------- */}
<div style={{fontSize:".9rem",marginTop:12}}>
  {loadingPath ? (
    "Finding best route…"
  ) : bestPath.length ? (
    <>
      {/* ── row 1: receive ─────────────────────────────────────────── */}
      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
        <span style={{whiteSpace:"nowrap"}}>Est receive:</span>
        <strong style={{display:"flex",alignItems:"center",gap:4}}>
          <img src={receiveLogo} alt="" width={14}/>
          {estOutStr} {receiveSymbol}
        </strong>
        <span style={{color:"#9fa6b2"}}>{usd(estOutHumanBN.times(receivePrice))}</span>
      </div>

      {/* ── row 2: current balance ─────────────────────────────────── */}
      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
        <span style={{whiteSpace:"nowrap"}}>Your balance:</span>
        <strong style={{display:"flex",alignItems:"center",gap:4}}>
          <img src={receiveLogo} alt="" width={14}/>
          {receiveBalStr} {receiveSymbol}
        </strong>
        <span style={{color:"#9fa6b2"}}>{usd(receiveBalBN.times(receivePrice))}</span>
      </div>

      {/* ── row 3: new balance ─────────────────────────────────────── */}
      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
        <span style={{whiteSpace:"nowrap"}}>Est new balance:</span>
         <strong style={{display:"flex",alignItems:"center",gap:4}}>
          <img src={receiveLogo} alt="" width={14}/>
            {newBalStr} {receiveSymbol}
        </strong>
        <span style={{color:"#9fa6b2"}}>{usd(newBalBN.times(receivePrice))}</span>
      </div>

      {/* ── route box ──────────────────────────────────────────────── */}
      <div
        style={{
          display:"flex",
          alignItems:"center",
          background:"#000",
          color:"#fff",
          padding:"8px 12px",
          borderRadius:4
        }}
      >
        <span style={{marginRight:12,whiteSpace:"nowrap"}}>Path:</span>
        <div style={{flex:1,display:"flex",justifyContent:"center",columnGap:6}}>
          {bestPath.map((p,idx)=>(
            <React.Fragment key={p.address}>
              <img src={p.logo} alt="" width={15} height={15} style={{verticalAlign:"-2px"}}/>
              {p.symbol}
              {idx < bestPath.length-1 && <span>&nbsp;→&nbsp;</span>}
            </React.Fragment>
          ))}
        </div>
      </div>
    </>
  ) : (
    <span>No route found</span>
  )}
</div>

      </Dialog>
    </>
  );
}

QuickSwap.displayName = "QuickSwap";
